import { motion } from 'framer-motion';
import { Key, Mail, Sparkles } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button, Input } from '@/components/ui';
import { useAuth } from '@/providers';

const emailSchema = z.string().email();
const DEBOUNCE_MS = 2000; // 2 seconds between attempts

interface LoginFormProps {
  initialMode?: 'login' | 'signup';
}

export function LoginForm({ initialMode = 'login' }: LoginFormProps) {
  const { t } = useTranslation();
  const {
    signInWithOAuth,
    signInWithMagicLink,
    signInWithPassword,
    signUp,
    error,
    isLoading,
    clearError,
  } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [usePasswordMode, setUsePasswordMode] = useState(initialMode === 'signup');
  const [isSignUpMode, setIsSignUpMode] = useState(initialMode === 'signup');
  const lastSendAttempt = useRef<number>(0);

  const handleOAuthSignIn = async (provider: 'apple' | 'azure' | 'google' | 'linkedin') => {
    await signInWithOAuth(provider);
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setEmailError(null);

      const result = emailSchema.safeParse(email);
      if (!result.success) {
        setEmailError(t('auth.errors.invalidEmail'));
        return;
      }

      // Prevent double-click
      if (isSending) {
        return;
      }

      if (usePasswordMode) {
        // Password sign-in or sign-up
        setIsSending(true);
        if (isSignUpMode) {
          const success = await signUp(email, password);
          if (success) {
            setMagicLinkSent(true); // Reuse the same confirmation screen
          }
        } else {
          await signInWithPassword(email, password);
        }
        setIsSending(false);
      } else {
        // Magic link - with debounce
        const now = Date.now();
        if (now - lastSendAttempt.current < DEBOUNCE_MS) {
          return;
        }

        setIsSending(true);
        lastSendAttempt.current = now;

        const success = await signInWithMagicLink(email);
        setIsSending(false);

        if (success) {
          setMagicLinkSent(true);
        }
      }
    },
    [
      email,
      password,
      isSending,
      usePasswordMode,
      isSignUpMode,
      signInWithMagicLink,
      signInWithPassword,
      signUp,
      t,
    ],
  );

  const togglePasswordMode = useCallback(() => {
    setUsePasswordMode((prev) => !prev);
    setPassword('');
    setIsSignUpMode(false);
    clearError();
  }, [clearError]);

  const _toggleSignUpMode = useCallback(() => {
    setIsSignUpMode((prev) => !prev);
    setPassword('');
    clearError();
  }, [clearError]);

  const handleBack = useCallback(() => {
    setMagicLinkSent(false);
    setEmailError(null);
    setIsSignUpMode(false);
    clearError();
  }, [clearError]);

  if (magicLinkSent) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-4"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <Mail className="w-8 h-8 text-green-600" />
        </div>
        <div className="space-y-2">
          <p className="text-[#2E2A23] font-medium">
            {isSignUpMode ? 'Conta criada!' : t('auth.magicLinkSent')}
          </p>
          <p className="text-[#888185] text-sm">
            {isSignUpMode
              ? `Enviamos um email de confirmação para ${email}. Clique no link para ativar sua conta.`
              : t('auth.sentTo', { email })}
          </p>
        </div>
        <Button variant="ghost" onClick={handleBack}>
          {t('common.back')}
        </Button>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="font-serif text-2xl text-[#2E2A23]">Bem-vindo à Newsletter</h2>
        <p className="text-[#888185] text-sm">
          O que você precisa ler sobre tecnologia.
          <br />
          Entre para começar a ler.
        </p>
      </div>

      {/* OAuth Buttons - Grid 2x2 */}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => handleOAuthSignIn('google')}
          disabled={isLoading || isSending}
          className="h-11 bg-white border border-[#CAC7C7] rounded-lg hover:bg-[#F4F2F2] hover:border-[#888185] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          aria-label="Continuar com Google"
        >
          <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <span className="text-[13px] font-medium text-[#2E2A23]">Google</span>
        </button>
        <button
          type="button"
          onClick={() => handleOAuthSignIn('apple')}
          disabled={isLoading || isSending}
          className="h-11 bg-white border border-[#CAC7C7] rounded-lg hover:bg-[#F4F2F2] hover:border-[#888185] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          aria-label="Continuar com Apple"
        >
          <svg
            className="w-5 h-5 flex-shrink-0"
            viewBox="0 0 24 24"
            fill="#000000"
            aria-hidden="true"
          >
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
          <span className="text-[13px] font-medium text-[#2E2A23]">Apple</span>
        </button>
        <button
          type="button"
          onClick={() => handleOAuthSignIn('azure')}
          disabled={isLoading || isSending}
          className="h-11 bg-white border border-[#CAC7C7] rounded-lg hover:bg-[#F4F2F2] hover:border-[#888185] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          aria-label="Continuar com Microsoft"
        >
          <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#F25022" d="M1 1h10v10H1z" />
            <path fill="#00A4EF" d="M1 13h10v10H1z" />
            <path fill="#7FBA00" d="M13 1h10v10H13z" />
            <path fill="#FFB900" d="M13 13h10v10H13z" />
          </svg>
          <span className="text-[13px] font-medium text-[#2E2A23]">Microsoft</span>
        </button>
        <button
          type="button"
          onClick={() => handleOAuthSignIn('linkedin')}
          disabled
          className="h-11 bg-white border border-[#CAC7C7] rounded-lg hover:bg-[#F4F2F2] hover:border-[#888185] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          aria-label="Continuar com LinkedIn"
        >
          <svg
            className="w-5 h-5 flex-shrink-0"
            viewBox="0 0 24 24"
            fill="#0A66C2"
            aria-hidden="true"
          >
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
          <span className="text-[13px] font-medium text-[#2E2A23]">LinkedIn</span>
        </button>
      </div>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-[#CAC7C7]" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-white px-4 text-[#888185]">ou</span>
        </div>
      </div>

      {/* Email Form - Always Visible */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888185]">
            <Mail className="w-5 h-5" />
          </div>
          <Input
            type="email"
            placeholder="Seu email de trabalho"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={emailError || undefined}
            className="pl-11"
          />
        </div>

        {usePasswordMode && (
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888185]">
              <Key className="w-5 h-5" />
            </div>
            <Input
              type="password"
              placeholder="Sua senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-11"
            />
          </div>
        )}

        <Button
          type="submit"
          isLoading={isSending || isLoading}
          disabled={isSending || isLoading || (usePasswordMode && !password)}
          className="w-full"
        >
          {usePasswordMode ? (isSignUpMode ? 'Criar conta' : 'Entrar') : 'Continuar'}
        </Button>
      </form>

      {/* Helper Text with toggle */}
      <p className="text-center text-sm text-[#888185]">
        {usePasswordMode ? (
          <>
            <Key className="w-4 h-4 inline-block mr-1 text-[#E94E1B]" />
            {isSignUpMode ? 'Crie sua conta com senha.' : 'Entre com sua senha.'}{' '}
            <button
              type="button"
              className="text-[#E94E1B] hover:underline cursor-pointer"
              onClick={togglePasswordMode}
            >
              Ou use link mágico
            </button>
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4 inline-block mr-1 text-[#E94E1B]" />
            Enviaremos um link mágico. Sem senha.{' '}
            <button
              type="button"
              className="text-[#E94E1B] hover:underline cursor-pointer"
              onClick={togglePasswordMode}
            >
              Ou entre com senha
            </button>
          </>
        )}
      </p>

      {/* Sign up / Sign in toggle - always visible */}
      <p className="text-center text-sm text-[#888185]">
        {isSignUpMode ? (
          <>
            Já tem uma conta?{' '}
            <button
              type="button"
              className="text-[#E94E1B] hover:underline font-medium cursor-pointer"
              onClick={() => {
                setIsSignUpMode(false);
                clearError();
              }}
            >
              Entrar
            </button>
          </>
        ) : (
          <>
            Não tem uma conta?{' '}
            <button
              type="button"
              className="text-[#E94E1B] hover:underline font-medium cursor-pointer"
              onClick={() => {
                setIsSignUpMode(true);
                setUsePasswordMode(true);
                setPassword('');
                clearError();
              }}
            >
              Criar conta
            </button>
          </>
        )}
      </p>

      {error && (
        <p className="text-sm text-red-500 text-center">
          {error.status === 429
            ? t('auth.errors.rateLimited')
            : error.status === 400
              ? t('auth.errors.invalidCredentials')
              : t('auth.errors.generic')}
        </p>
      )}

      {/* Terms */}
      <p className="text-center text-xs text-[#888185]">
        Ao entrar, você concorda com nossos{' '}
        <a href="/terms" className="underline hover:text-[#E94E1B]">
          Termos de Uso
        </a>{' '}
        e{' '}
        <a href="/privacy" className="underline hover:text-[#E94E1B]">
          Política de Privacidade
        </a>
        .
      </p>
    </div>
  );
}
